import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calculator, ChevronLeft, ChevronRight, AlertTriangle, RefreshCw } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import basService from '../../services/bas';
import xeroConnectionService from '../../services/xeroConnection';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import { useOrgTimezone, getTodayInTz, formatLocalDateInTz } from '../../utils/timezone';
import type { BasSummary as Summary, BasSource } from '../../types/bas';

/**
 * /accounts/bas — the month's BAS figures, accrual basis (HHNodeServer
 * basSummaryService). Sales come from the invoices and credit notes raised
 * here; purchases from Xero, where accounts payable is kept; PAYG withholding
 * from Xero Payroll's pay runs. The server works every figure out; this page
 * shows them, and says what it could not read.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const shiftMonth = (month: string, delta: number): string => {
  const [y, m] = month.split('-').map(Number);
  const i = y * 12 + (m - 1) + delta;
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
};
const monthLabel = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};
const validMonth = (v: string | null) => !!v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

function Label({ code, name, value, strong }: { code: string; name: string; value: number | null; strong?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 py-2 ${strong ? 'font-semibold' : ''}`}>
      <span className="min-w-0 break-words text-sm text-neutral-700">
        <span className="inline-block w-9 font-mono text-xs font-semibold text-neutral-600">{code}</span>
        {name}
      </span>
      <span className="shrink-0 text-sm tabular-nums text-neutral-900">{value === null ? 'Not read' : money(value)}</span>
    </div>
  );
}

function SourceTable({ title, sources, empty }: { title: string; sources: BasSource[]; empty: string }) {
  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
      <h2 className="text-base font-semibold text-neutral-900 mb-2">{title}</h2>
      {sources.length === 0 ? (
        <p className="text-sm text-neutral-600 break-words">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-600">
                <th className="py-1 pr-2 font-medium">From</th>
                <th className="py-1 px-2 font-medium text-right">How many</th>
                <th className="py-1 px-2 font-medium text-right">Inc GST</th>
                <th className="py-1 pl-2 font-medium text-right">GST</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.key} className="border-t border-neutral-100">
                  <td className="py-1.5 pr-2 text-neutral-800 break-words">{s.label}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{s.count}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">{money(s.gross)}</td>
                  <td className="py-1.5 pl-2 text-right tabular-nums whitespace-nowrap">{money(s.gst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BasSummary() {
  const tz = useOrgTimezone();
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');
  const [params, setParams] = useSearchParams();
  const lastMonth = shiftMonth(getTodayInTz(tz).slice(0, 7), -1);
  const month = validMonth(params.get('month')) ? params.get('month')! : lastMonth;

  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await basService.getMonth(month);
      if (res.success && res.data) setData(res.data);
      else { setData(null); setError(errorMessage(res, 'The BAS summary could not be worked out')); }
    } catch (e) {
      setData(null); setError(errorMessage(e, 'The BAS summary could not be worked out'));
    }
    setLoading(false);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const go = (m: string) => setParams({ month: m }, { replace: true });

  const reconnect = async () => {
    setReconnecting(true);
    try {
      // Spend and Receive Money are bank transactions: the card purchases permission.
      const res = await xeroConnectionService.getAuthorizeUrl(false, true);
      if (res.success && res.data?.authorization_url) { window.location.href = res.data.authorization_url; return; }
      setError(errorMessage(res, 'Could not open Xero'));
    } catch (e) {
      setError(errorMessage(e, 'Could not open Xero'));
    }
    setReconnecting(false);
  };

  const l = data?.labels;
  const owed = l ? l.total >= 0 : true;

  return (
    <>
      <PageHeader
        title="BAS summary"
        subtitle="GST and PAYG withholding for a month: your sales from here, your purchases and pay runs from Xero"
        icon={<Calculator className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => go(shiftMonth(month, -1))} aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <label htmlFor="bas-month" className="sr-only">Month</label>
          <input
            id="bas-month" type="month" value={month} max={getTodayInTz(tz).slice(0, 7)}
            onChange={(e) => { if (validMonth(e.target.value)) go(e.target.value); }}
            className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm min-w-0"
          />
          <Button variant="outline" size="sm" onClick={() => go(shiftMonth(month, 1))} aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <span className="text-sm text-neutral-600 break-words">Accrual basis: by invoice and bill date.</span>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {loading && !data ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-neutral-100 rounded-xl animate-pulse" />)}
          </div>
        ) : data && l ? (
          <>
            <section className={`rounded-xl border p-4 ${owed ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <p className="text-sm text-neutral-700">{monthLabel(data.month)}</p>
              <p className="text-2xl font-semibold text-neutral-900 break-words">
                {owed ? 'To pay the ATO' : 'Refund from the ATO'} {money(Math.abs(l.total))}
              </p>
              <p className="text-sm text-neutral-700 break-words">
                Net GST {money(l.net_gst)}{l.W2 !== null ? ` plus PAYG withheld ${money(l.W2)}` : ', PAYG withholding not read'}.
                {!data.complete && ' Some figures could not be read: see below.'}
              </p>
            </section>

            {data.warnings.length > 0 && (
              <ul className="space-y-2">
                {data.warnings.map((w) => (
                  <li key={w.code} className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="min-w-0 break-words">
                      {w.message}
                      {w.code === 'BANK_TRANSACTIONS_PERMISSION' && (isAdmin ? (
                        <span className="block mt-2">
                          <Button size="sm" onClick={reconnect} disabled={reconnecting}>{reconnecting ? 'Opening Xero…' : 'Reconnect Xero'}</Button>
                        </span>
                      ) : ' Ask an Accounts Admin.')}
                      {w.code === 'XERO_NOT_CONNECTED' && <> <Link to="/accounts/xero" className="underline">Open Xero settings</Link></>}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0 divide-y divide-neutral-100">
                <h2 className="text-base font-semibold text-neutral-900 pb-2">GST on sales</h2>
                <Label code="G1" name="Total sales (inc GST)" value={l.G1} />
                <Label code="G2" name="Export sales" value={l.G2} />
                <Label code="G3" name="Other GST-free sales" value={l.G3} />
                <Label code="1A" name="GST on sales" value={l['1A']} strong />
              </section>
              <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0 divide-y divide-neutral-100">
                <h2 className="text-base font-semibold text-neutral-900 pb-2">GST on purchases</h2>
                <Label code="G10" name="Capital purchases" value={l.G10} />
                <Label code="G11" name="Non-capital purchases" value={l.G11} />
                <Label code="1B" name="GST on purchases" value={l['1B']} strong />
              </section>
              <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0 divide-y divide-neutral-100">
                <h2 className="text-base font-semibold text-neutral-900 pb-2">PAYG withholding</h2>
                <Label code="W1" name="Total salary and wages" value={l.W1} />
                <Label code="W2" name="Withheld from them" value={l.W2} strong />
                {data.payroll.status === 'not_connected' && (
                  <p className="pt-2 text-xs text-neutral-600 break-words">Xero Payroll is not connected, so pay runs are not read.</p>
                )}
              </section>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <SourceTable title="Where the sales come from" sources={data.sales.sources} empty="No sales this month." />
              <SourceTable
                title={`Purchases from Xero${data.xero.tenant_name ? ` (${data.xero.tenant_name})` : ''}`}
                sources={data.purchases.sources}
                empty={data.xero.connected ? 'No bills or Spend Money in Xero this month.' : 'Xero is not connected.'}
              />
            </div>

            {data.payroll.runs.length > 0 && (
              <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
                <h2 className="text-base font-semibold text-neutral-900 mb-2">Pay runs paid this month</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-neutral-600">
                        <th className="py-1 pr-2 font-medium">Paid</th>
                        <th className="py-1 px-2 font-medium">Period</th>
                        <th className="py-1 px-2 font-medium text-right">Wages</th>
                        <th className="py-1 px-2 font-medium text-right">PAYG</th>
                        <th className="py-1 pl-2 font-medium text-right">Super</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payroll.runs.map((r, i) => (
                        <tr key={`${r.payment_date}-${i}`} className="border-t border-neutral-100">
                          <td className="py-1.5 pr-2 whitespace-nowrap">{r.payment_date ? formatLocalDateInTz(r.payment_date, tz) : '—'}</td>
                          <td className="py-1.5 px-2 whitespace-nowrap">
                            {r.period_start && r.period_end ? `${formatLocalDateInTz(r.period_start, tz)} – ${formatLocalDateInTz(r.period_end, tz)}` : '—'}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{money(r.gross)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{money(r.tax)}</td>
                          <td className="py-1.5 pl-2 text-right tabular-nums">{money(r.super)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-neutral-600 mt-2 break-words">Super is shown for reference; it is not on the BAS.</p>
              </section>
            )}

            {data.not_ours.length > 0 && (
              <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
                <h2 className="text-base font-semibold text-neutral-900 mb-1">Sales in Xero not raised here</h2>
                <p className="text-sm text-neutral-600 mb-2 break-words">Counted in the sales above, so the BAS matches Xero.</p>
                <ul className="divide-y divide-neutral-100 text-sm">
                  {data.not_ours.map((d, i) => (
                    <li key={`${d.number}-${i}`} className="flex flex-wrap justify-between gap-x-3 py-1.5">
                      <span className="min-w-0 break-words">
                        {d.type === 'RECEIVE' ? 'Receive Money' : d.type === 'ACCRECCREDIT' ? 'Credit note' : 'Invoice'}
                        {d.number ? ` ${d.number}` : ''}{d.contact ? ` · ${d.contact}` : ''}
                        {d.date ? ` · ${formatLocalDateInTz(d.date, tz)}` : ''}
                      </span>
                      <span className="tabular-nums whitespace-nowrap">{money(d.gross)} (GST {money(d.gst)})</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="text-sm text-neutral-600 space-y-1 break-words">
              {data.deposits.count > 0 && (
                <p>{data.deposits.count} deposit invoice{data.deposits.count === 1 ? '' : 's'} ({money(data.deposits.amount)}) left out: a deposit is BAS Excluded until the trees are invoiced.</p>
              )}
              {data.excluded_from_bas !== 0 && <p>{money(data.excluded_from_bas)} on Xero lines marked BAS Excluded is left out.</p>}
              <p>Manual journals in Xero are not read. Check these figures against Xero's Activity Statement before you lodge; this page is not tax advice.</p>
            </section>
          </>
        ) : null}
      </div>
    </>
  );
}

export default BasSummary;
